import { createSlice } from '@reduxjs/toolkit';
import { assign, uniq } from 'lodash';
import { amenityMappingText } from 'utils/hotelFilter';

export interface FavoriteItem {
   id?: string;
   hotelId?: string;
   [key: string]: unknown;
}

export interface FavoriteList {
   id?: string;
   name?: string;
   favoriteHotels?: FavoriteItem[];
   favoriteHotelCount?: number;
   isDefault?: boolean;
   [key: string]: unknown;
}

export interface FavoritesState {
   favoritesList: FavoriteList[];
   favoritesHotels: string[];
   favoritesMappings: Record<string, string[]>;
   [key: string]: unknown;
}

export interface HotelState {
   hotelStatic: Record<string, unknown>;
   commission: Record<string, unknown>;
   hotelImages: Record<string, unknown>;
   favorites: FavoritesState;
   [key: string]: unknown;
}

type favoritesType = {
   favoritesList?: FavoriteList[];
   favoritesHotels?: string[];
   favoritesMappings?: Record<string, string[]>;
};

const initialState: HotelState = {
   hotelStatic: {
      amenity_translations: [],
      beddings: [],
      meals: {},
      room_amenities: {},
      serp_filters: {},
      socket_types: {},
      taxes: {},
   },
   commission: {},
   hotelImages: {},
   favorites: {
      favoritesList: [],
      favoritesHotels: [],
      favoritesMappings: {},
   },
};

export const hotelSlice = createSlice({
   name: 'hotel',
   initialState,
   reducers: {
      setHotelStatic: (state, action) => {
         const serpFilters = action?.payload?.serp_filters;
         let mappedFilters = serpFilters;

         if (serpFilters) {
            mappedFilters = { ...serpFilters };
            for (const key in mappedFilters) {
               const mapped = amenityMappingText[mappedFilters[key]];
               if (mapped) {
                  mappedFilters[key] = mapped;
               }
            }
         }

         state.hotelStatic = {
            ...action.payload,
            serp_filters: mappedFilters,
         };
      },
      setCommission: (state: HotelState, { payload }: { payload: Record<string, unknown> }) => {
         state.commission = assign({}, state.commission, payload);
      },
      cleanCommission: (state: HotelState) => {
         state.commission = {};
      },
      setImage: (state: HotelState, { payload }: { payload: Record<string, unknown> }) => {
         state.hotelImages = assign({}, state.hotelImages, payload);
      },
      cleanImages: (state: HotelState) => {
         state.hotelImages = {};
      },
      setFavorites: (state: HotelState, { payload }: { payload: favoritesType }) => {
         state.favorites = assign({}, state.favorites, payload);
      },
      setFavoritesHotels: (state: HotelState, { payload }: { payload: string[] }) => {
         const currentHotels = state.favorites?.favoritesHotels || [];
         state.favorites = assign({}, state.favorites, { favoritesHotels: uniq([...currentHotels, ...payload]) });
      },
      removeFavoritesHotels: (state: HotelState, { payload }: { payload: string }) => {
         const targetId = String(payload);
         const updatedHotels = (state.favorites?.favoritesHotels || []).filter((f: string) => String(f) !== targetId);

         const updatedMappings: Record<string, string[]> = {};
         if (state.favorites?.favoritesMappings) {
            Object.keys(state.favorites.favoritesMappings).forEach((key: string) => {
               const list = state.favorites.favoritesMappings?.[key];
               if (Array.isArray(list)) {
                  updatedMappings[key] = list.filter((id: string) => String(id) !== targetId);
               }
            });
         }

         const updatedList = (state.favorites?.favoritesList || []).map((fav: FavoriteList) => {
            if (!fav?.favoriteHotels) return fav;
            const newFavHotels = fav.favoriteHotels.filter((item: FavoriteItem | string) => {
               const itemId = typeof item === 'object' && item !== null ? item.hotelId : item;
               return String(itemId) !== targetId;
            });
            return {
               ...fav,
               favoriteHotels: newFavHotels,
               favoriteHotelCount: newFavHotels.length,
            };
         });

         state.favorites = assign({}, state.favorites, {
            favoritesHotels: updatedHotels,
            favoritesMappings: assign({}, state.favorites?.favoritesMappings, updatedMappings),
            favoritesList: updatedList,
         });
      },
      setFavoritesMappings: (state: HotelState, { payload }: { payload: Record<string, unknown> }) => {
         state.favorites = assign({}, state.favorites, { favoritesMappings: { ...state.favorites?.favoritesMappings, ...payload } });
      },
   },
});

export default hotelSlice;
